import { useEffect, useMemo, useState } from "react";
import { Capacitor } from "@capacitor/core";
import OrbitPrototype from "../orbit-prototype.jsx";
import { supabase } from "./supabaseClient";
import PrivacyPolicy from "./components/legal/PrivacyPolicy.jsx";
import TermsOfService from "./components/legal/TermsOfService.jsx";
import ConsentBanner, { useConsent } from "./components/legal/ConsentBanner.jsx";
import { registerPushNotifications } from "./utils/pushNotifications.js";
import { scheduleBackgroundSync } from "./utils/backgroundSync.js";

async function signInWithApple(setError, setLoading) {
  setError("");
  setLoading(true);
  try {
    const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");
    const { response } = await SignInWithApple.authorize({
      clientId: "io.orbitapp.app",
      redirectURI: window.location.origin,
      scopes: "name email",
    });
    const { data: appleData, error } = await supabase.auth.signInWithIdToken({
      provider: "apple",
      token: response.identityToken,
    });
    if (error) throw error;

    // Apple only provides name/email on the very first authorization.
    // Capture them immediately before they're lost.
    const givenName = response.givenName;
    const familyName = response.familyName;
    const appleEmail = response.email;
    if ((givenName || familyName || appleEmail) && appleData?.user?.id) {
      const displayName = [givenName, familyName].filter(Boolean).join(" ").trim() || undefined;
      await supabase.from("user_profiles").upsert(
        {
          user_id: appleData.user.id,
          ...(displayName && { display_name: displayName }),
          ...(appleEmail && { email: appleEmail }),
        },
        { onConflict: "user_id", ignoreDuplicates: true }
      );
    }
  } catch (err) {
    if (err?.message !== "ASAUTHORIZATION_ERROR" /* user cancelled */) {
      setError(err?.message || "Apple Sign In failed.");
    }
  } finally {
    setLoading(false);
  }
}

function AuthScreen() {
  const [mode, setMode] = useState("login"); // login | signup | forgot | mfa
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  // MFA state
  const [mfaFactorId, setMfaFactorId] = useState(null);
  const [mfaChallengeId, setMfaChallengeId] = useState(null);
  const [mfaCode, setMfaCode] = useState("");

  const canSubmit = mode === "forgot"
    ? email.trim().length > 0
    : mode === "mfa"
      ? mfaCode.length === 6
      : email.trim() && password.length >= 6;

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);
    try {
      if (mode === "forgot") {
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
          redirectTo: window.location.origin,
        });
        if (resetError) throw resetError;
        setInfo("Password reset email sent. Check your inbox and follow the link to set a new password.");
      } else if (mode === "mfa") {
        const { error: verifyError } = await supabase.auth.mfa.verify({
          factorId: mfaFactorId,
          challengeId: mfaChallengeId,
          code: mfaCode,
        });
        if (verifyError) throw verifyError;
        // MFA verified â€” session is now fully authenticated (onAuthStateChange fires)
      } else if (mode === "login") {
        const { data, error: signInError } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password
        });
        if (signInError) throw signInError;
        // Check if MFA is required
        const { data: factors } = await supabase.auth.mfa.listFactors();
        const totp = factors?.totp?.filter(f => f.status === "verified") || [];
        if (totp.length > 0) {
          const factor = totp[0];
          const { data: challenge, error: chalError } = await supabase.auth.mfa.challenge({ factorId: factor.id });
          if (chalError) throw chalError;
          setMfaFactorId(factor.id);
          setMfaChallengeId(challenge.id);
          setMfaCode("");
          setMode("mfa");
          return; // Don't clear loading yet â€” show MFA screen
        }
      } else {
        const { data, error: signUpError } = await supabase.auth.signUp({
          email: email.trim(),
          password
        });
        if (signUpError) throw signUpError;
        if (!data?.session) {
          setInfo("Check your email to confirm your account, then log in.");
        }
      }
    } catch (err) {
      setError(err?.message || "Authentication failed.");
    } finally {
      setLoading(false);
    }
  }

  const styles = useMemo(
    () => ({
      shell: {
        minHeight: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: "#F7F2EB",
        fontFamily: "'DM Sans', system-ui, -apple-system, Segoe UI, Roboto, sans-serif"
      },
      card: {
        width: "100%",
        maxWidth: 420,
        background: "#FFFFFF",
        border: "1px solid #E4D8C8",
        borderRadius: 18,
        boxShadow: "0 8px 48px rgba(60,40,20,.12)",
        overflow: "hidden"
      },
      head: {
        padding: "16px 18px 12px",
        background: "#FDF8F2",
        borderBottom: "1px solid #E4D8C8",
        display: "flex",
        alignItems: "center",
        gap: 10
      },
      mark: {
        width: 28,
        height: 28,
        borderRadius: "50%",
        background: "linear-gradient(135deg,#C17B4E,#7B6BAE)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12,
        color: "white"
      },
      title: { fontSize: 18, fontFamily: "'Crimson Pro', serif", color: "#1E1008" },
      body: { padding: 18 },
      label: { fontSize: 12, color: "#7A5C42", fontWeight: 600, marginBottom: 6 },
      input: {
        width: "100%",
        padding: "12px 12px",
        borderRadius: 12,
        border: "1px solid #CDBFA8",
        outline: "none",
        fontSize: 14
      },
      btn: {
        width: "100%",
        padding: "12px 14px",
        borderRadius: 14,
        border: "none",
        cursor: "pointer",
        background: "linear-gradient(135deg,#4A3020,#C17B4E)",
        color: "white",
        fontWeight: 700,
        fontSize: 14,
        boxShadow: "0 4px 20px rgba(74,48,32,.25)"
      },
      btnGhost: {
        border: "1px solid #E4D8C8",
        background: "transparent",
        padding: "10px 12px",
        borderRadius: 12,
        cursor: "pointer",
        fontSize: 12,
        color: "#7A5C42",
        width: "100%"
      },
      msgErr: {
        marginTop: 12,
        background: "rgba(184,96,96,0.10)",
        border: "1px solid rgba(184,96,96,0.25)",
        color: "#B86060",
        padding: "10px 12px",
        borderRadius: 12,
        fontSize: 12,
        lineHeight: 1.5
      },
      msgInfo: {
        marginTop: 12,
        background: "rgba(94,140,101,0.10)",
        border: "1px solid rgba(94,140,101,0.25)",
        color: "#3E6044",
        padding: "10px 12px",
        borderRadius: 12,
        fontSize: 12,
        lineHeight: 1.5
      },
      legalLink: {
        background: "none", border: "none", cursor: "pointer",
        color: "#A08870", fontSize: 11, textDecoration: "underline", padding: 0,
      }
    }),
    []
  );

  return (
    <div style={styles.shell}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; }
      `}</style>
      <div style={styles.card}>
        <div style={styles.head}>
          <img src="/orbit-logo.png" alt="Orbit" style={{ width: 28, height: 28, objectFit: "contain", flexShrink: 0 }} />
          <div>
            <div style={styles.title}>orbit</div>
            <div style={{ fontSize: 12, color: "#A08870" }}>
              {mode === "forgot" ? "Reset your password" : mode === "mfa" ? "Two-factor authentication" : mode === "login" ? "Log in to continue" : "Create your account"}
            </div>
          </div>
        </div>
        <div style={styles.body}>
          <form onSubmit={onSubmit} aria-label={mode === "login" ? "Log in" : mode === "signup" ? "Create account" : mode === "forgot" ? "Reset password" : "Two-factor authentication"}>
            {mode === "mfa" ? (
              <div style={{ marginBottom: 14 }}>
                <label htmlFor="mfa-code" style={styles.label}>Enter the 6-digit code from your authenticator app</label>
                <input
                  id="mfa-code"
                  style={{ ...styles.input, textAlign: "center", fontSize: 24, letterSpacing: 8, fontFamily: "monospace" }}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  autoFocus
                  aria-label="Authentication code"
                />
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label htmlFor="auth-email" style={styles.label}>Email</label>
                  <input
                    id="auth-email"
                    style={styles.input}
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                </div>
                {mode !== "forgot" && (
                  <div style={{ marginBottom: 14 }}>
                    <label htmlFor="auth-password" style={styles.label}>Password</label>
                    <input
                      id="auth-password"
                      style={styles.input}
                      type="password"
                      autoComplete={mode === "login" ? "current-password" : "new-password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      required
                      minLength={6}
                    />
                  </div>
                )}
              </>
            )}
            <button style={styles.btn} disabled={!canSubmit || loading} type="submit">
              {loading ? "Please wait..." : mode === "forgot" ? "Send reset link" : mode === "mfa" ? "Verify" : mode === "login" ? "Log in" : "Sign up"}
            </button>
          </form>

          <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
            {mode === "login" && (
              <button
                type="button"
                style={{ ...styles.btnGhost, fontSize: 12, border: "none", color: "#A08870" }}
                onClick={() => { setError(""); setInfo(""); setMode("forgot"); }}
              >
                Forgot your password?
              </button>
            )}
            {mode !== "mfa" && (
              <button
                type="button"
                style={styles.btnGhost}
                onClick={() => {
                  setError("");
                  setInfo("");
                  setMode((m) => m === "login" || m === "forgot" ? "signup" : "login");
                }}
              >
                {mode === "signup" ? "Already have an account? Log in" : "Need an account? Sign up"}
              </button>
            )}
            {mode === "mfa" && (
              <button
                type="button"
                style={styles.btnGhost}
                onClick={() => { setError(""); setInfo(""); setMfaFactorId(null); setMfaChallengeId(null); setMfaCode(""); setMode("login"); }}
              >
                Back to login
              </button>
            )}
          </div>

          {/* Sign in with Apple â€” only shown on native iOS (required by App Store) */}
          {Capacitor.isNativePlatform() && mode !== "mfa" && (
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                style={{ width: "100%", padding: "12px 14px", borderRadius: 14, border: "none", cursor: "pointer", background: "#000000", color: "white", fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                onClick={() => signInWithApple(setError, setLoading)}
                disabled={loading}
              >
                <svg width="16" height="16" viewBox="0 0 814 1000" fill="white"><path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105-34.5-155.5-127.2C46.8 790.2 0 682.7 0 576.8c0-143.7 93.7-219.9 185.3-219.9 79.1 0 143.4 52.9 192.4 52.9 46.4 0 120.5-55.3 209.4-55.3zM614 88.4c-5.1 38.1-24.5 81-55.5 110.4-33.3 32-72.7 56.4-114.3 56.4-4.5 0-9-.6-13.5-1.3-1.3-38.7 18.6-80.5 47.7-112.9 29.1-32 75.9-56.8 115.7-59.7 1.9.1 3.9.1 5.9.1 4.7 0 9.4-.3 14-.9z"/></svg>
                Sign in with Apple
              </button>
            </div>
          )}

          {error ? <div role="alert" aria-live="assertive" style={styles.msgErr}>{error}</div> : null}
          {info ? <div role="status" aria-live="polite" style={styles.msgInfo}>{info}</div> : null}

          <div style={{ marginTop: 18, textAlign: "center", fontSize: 11, color: "#B09880" }}>
            By continuing you agree to our{" "}
            <button type="button" style={styles.legalLink} onClick={() => setShowTerms(true)}>Terms of Service</button>
            {" "}and{" "}
            <button type="button" style={styles.legalLink} onClick={() => setShowPrivacy(true)}>Privacy Policy</button>
          </div>
        </div>
      </div>

      {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}
      {showTerms && <TermsOfService onClose={() => setShowTerms(false)} />}
    </div>
  );
}

function PasswordRecoveryScreen({ onDone }) {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  async function handleReset(e) {
    e.preventDefault();
    setError("");
    if (pw.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (pw !== confirm) { setError("Passwords don't match."); return; }
    setLoading(true);
    try {
      const { error: updateErr } = await supabase.auth.updateUser({ password: pw });
      if (updateErr) throw updateErr;
      setDone(true);
      setTimeout(() => onDone(), 2000);
    } catch (err) {
      setError(err?.message || "Failed to update password.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: "#F7F2EB", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <div style={{ width: "100%", maxWidth: 420, background: "#FFFFFF", border: "1px solid #E4D8C8", borderRadius: 18, boxShadow: "0 8px 48px rgba(60,40,20,.12)", overflow: "hidden" }}>
        <div style={{ padding: "16px 18px 12px", background: "#FDF8F2", borderBottom: "1px solid #E4D8C8", display: "flex", alignItems: "center", gap: 10 }}>
          <img src="/orbit-logo.png" alt="Orbit" style={{ width: 28, height: 28, objectFit: "contain", flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 18, fontFamily: "'Crimson Pro', serif", color: "#1E1008" }}>orbit</div>
            <div style={{ fontSize: 12, color: "#A08870" }}>Set your new password</div>
          </div>
        </div>
        <div style={{ padding: 18 }}>
          {done ? (
            <div style={{ background: "rgba(94,140,101,0.10)", border: "1px solid rgba(94,140,101,0.25)", color: "#3E6044", padding: "14px 12px", borderRadius: 12, fontSize: 13, textAlign: "center" }}>
              Password updated! Redirecting...
            </div>
          ) : (
            <form onSubmit={handleReset}>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: "#7A5C42", fontWeight: 600, marginBottom: 6 }}>New password</div>
                <input style={{ width: "100%", padding: "12px 12px", borderRadius: 12, border: "1px solid #CDBFA8", outline: "none", fontSize: 14, boxSizing: "border-box" }} type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 6 characters" required minLength={6} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: "#7A5C42", fontWeight: 600, marginBottom: 6 }}>Confirm password</div>
                <input style={{ width: "100%", padding: "12px 12px", borderRadius: 12, border: "1px solid #CDBFA8", outline: "none", fontSize: 14, boxSizing: "border-box" }} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat new password" required minLength={6} />
              </div>
              {error && <div style={{ marginBottom: 12, background: "rgba(184,96,96,0.10)", border: "1px solid rgba(184,96,96,0.25)", color: "#B86060", padding: "10px 12px", borderRadius: 12, fontSize: 12 }}>{error}</div>}
              <button style={{ width: "100%", padding: "12px 14px", borderRadius: 14, border: "none", cursor: "pointer", background: "linear-gradient(135deg,#4A3020,#C17B4E)", color: "white", fontWeight: 700, fontSize: 14 }} disabled={loading} type="submit">
                {loading ? "Updating..." : "Set new password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  const { consent, accept: acceptConsent, decline: declineConsent } = useConsent();
  const [showConsentPrivacy, setShowConsentPrivacy] = useState(false);
  const needsConsent = consent === null;

  useEffect(() => {
    let mounted = true;
    supabase.auth
      .getSession()
      .then(({ data, error }) => {
        if (error) throw error;
        if (mounted) setSession(data.session ?? null);
      })
      .catch(() => {
        if (mounted) setSession(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    const { data: sub } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (event === "PASSWORD_RECOVERY") {
        setPasswordRecovery(true);
      }
      setSession(nextSession ?? null);

      // Register for push notifications and background sync on first sign-in
      // (native only â€” both are no-ops on web)
      if ((event === "SIGNED_IN" || event === "TOKEN_REFRESHED") && nextSession?.user?.id) {
        registerPushNotifications(supabase, nextSession.user.id);
        scheduleBackgroundSync(nextSession.user.id, nextSession);
      }

      // Remove the device's push token from the server on logout so the
      // device stops receiving notifications for the previous account.
      // Also clear per-user localStorage so a different account that logs in
      // on the same device starts with a clean slate.
      if (event === "SIGNED_OUT") {
        [
          'orbit_display_name',
          'orbit_read_notif_ids',
          'orbit_prefs',
          'orbit_notif_settings',
          'orbit_priv',
          'orbit_accounts',
          'orbit_cal_names',
          'orbit_last_screen',
          'orbit_hide_offline',
          'orbit_block_notes',
          'orbit_compact',
          'orbit_theme',
          'orbit_custom_theme',
          'orbit_push_token',
        ].forEach(k => { try { localStorage.removeItem(k); } catch {} });
      }
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  if (loading) return (
    <div style={{ minHeight: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#F7F2EB" }}>
      <div style={{ width: 36, height: 36, borderRadius: "50%", border: "3px solid #E4D8C8", borderTopColor: "#C17B4E", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  const consentBanner = needsConsent ? (
    <>
      <ConsentBanner
        onAccept={acceptConsent}
        onDecline={declineConsent}
        onShowPrivacy={() => setShowConsentPrivacy(true)}
      />
      {showConsentPrivacy && <PrivacyPolicy onClose={() => setShowConsentPrivacy(false)} />}
    </>
  ) : null;

  if (!session) return <>{<AuthScreen />}{consentBanner}</>;
  if (passwordRecovery) return <><PasswordRecoveryScreen onDone={() => setPasswordRecovery(false)} />{consentBanner}</>;
  return <>{<OrbitPrototype key={session.user.id} user={session.user} />}{consentBanner}</>;
}

